/**
 * Krishnamurti Paddhati (KP) engine: sub-lords, significators, and ruling planets.
 *
 * The KP sub-lord is the sharpest birth-time fingerprint in practical rectification.
 * The zodiac is subdivided by Vimshottari proportions into sign -> star (nakshatra)
 * -> sub -> sub-sub. The Ascendant's sub-lord changes every few minutes, so it
 * discriminates birth time far more finely than the sign (which changes every ~2h)
 * or even the nakshatra (~53 min). A KP rectifier pins the time by requiring the
 * Ascendant/cuspal sub-lords and the running dasha lords to signify the houses an
 * event activates.
 */

import * as swe from "./ephemeris";
import { pymod } from "../lib/pyutil";
import type { PlainDateTime } from "../lib/datetime";
import { addDays, combine, dateOnly, timeOnly, weekday } from "../lib/datetime";
import { DASHA_SEQUENCE, SIGN_RULERS } from "./constants";
import { NAKSHATRA_LORDS, NAKSHATRA_SPAN_DEG, get_drishti_strength } from "./helpers";
import { ensure_swiss_ephemeris, local_to_jd } from "./chart";
import type { RawChartData } from "./models";

const _TOTAL_YEARS = DASHA_SEQUENCE.reduce((acc, [, years]) => acc + years, 0); // 120
const _SEQ = DASHA_SEQUENCE.map(([lord]) => lord);
const _YEARS: Record<string, number> = Object.fromEntries(DASHA_SEQUENCE);

// Weekday (Sun=0..Sat=6 in Python's date.weekday()+? ) -> day lord. Python weekday(): Mon=0..Sun=6.
const _WEEKDAY_LORD: Record<number, string> = {
  0: "moon", // Monday
  1: "mars", // Tuesday
  2: "mercury", // Wednesday
  3: "jupiter", // Thursday
  4: "venus", // Friday
  5: "saturn", // Saturday
  6: "sun", // Sunday
};

// const _ANGLES = new Set(["ascendant", "midheaven", "descendant", "imumcoeli"]);  // unused in Python too
const _GRAHAS = ["sun", "moon", "mars", "mercury", "jupiter", "venus", "saturn", "rahu", "ketu"] as const;

function _seq_from(lord: string): string[] {
  const index = _SEQ.indexOf(lord);
  return [..._SEQ.slice(index), ..._SEQ.slice(0, index)];
}

/**
 * Return the sign / star / sub / sub-sub lords for a sidereal longitude.
 *
 * Sub and sub-sub are the Vimshottari proportional subdivisions, started from the
 * star lord and sub lord respectively — the standard KP construction.
 */
export function kp_sublord_chain(longitude: number): Record<string, string> {
  const lon = pymod(longitude, 360.0);
  const sign = Math.trunc(Math.floor(lon / 30));
  const sign_lord = SIGN_RULERS[sign];

  const star_idx = pymod(Math.trunc(lon / NAKSHATRA_SPAN_DEG), 27);
  const star_lord = NAKSHATRA_LORDS[star_idx];
  const pos_in_star = lon - star_idx * NAKSHATRA_SPAN_DEG;

  // Sub: subdivide the star span (13°20') by Vimshottari weights from the star lord.
  let sub_lord = star_lord;
  let sub_start = 0.0;
  let sub_span = NAKSHATRA_SPAN_DEG;
  let cumulative = 0.0;
  const star_sequence = _seq_from(star_lord);
  for (const lord of star_sequence) {
    const segment = (NAKSHATRA_SPAN_DEG * _YEARS[lord]) / _TOTAL_YEARS;
    if (pos_in_star < cumulative + segment || lord === star_sequence[star_sequence.length - 1]) {
      sub_lord = lord;
      sub_start = cumulative;
      sub_span = segment;
      break;
    }
    cumulative += segment;
  }
  const pos_in_sub = pos_in_star - sub_start;

  // Sub-sub: subdivide the sub span by the same weights from the sub lord.
  let sub_sub_lord = sub_lord;
  cumulative = 0.0;
  const sub_sequence = _seq_from(sub_lord);
  for (const lord of sub_sequence) {
    const segment = (sub_span * _YEARS[lord]) / _TOTAL_YEARS;
    if (pos_in_sub < cumulative + segment || lord === sub_sequence[sub_sequence.length - 1]) {
      sub_sub_lord = lord;
      break;
    }
    cumulative += segment;
  }

  return {
    sign_lord: sign_lord,
    star_lord: star_lord,
    sub_lord: sub_lord,
    sub_sub_lord: sub_sub_lord,
  };
}

/** House (1-12) a longitude falls in, given 12 sidereal Placidus cusp longitudes. */
export function placidus_house_of(longitude: number, cusps: number[]): number {
  if (!cusps || cusps.length < 12) {
    return 1;
  }
  const lon = pymod(longitude, 360.0);
  for (let i = 0; i < 12; i++) {
    const start = pymod(cusps[i], 360.0);
    const end = pymod(cusps[(i + 1) % 12], 360.0);
    if (start < end) {
      if (start <= lon && lon < end) {
        return i + 1;
      }
    } else {
      // cusp pair wraps past 0°
      if (lon >= start || lon < end) {
        return i + 1;
      }
    }
  }
  return 1;
}

function _star_lord_of(longitude: number): string {
  // Normalise first (same as kp_sublord_chain): Math.trunc on a NEGATIVE longitude truncates
  // toward zero, which lands the pymod on the wrong nakshatra index.
  const lon = pymod(longitude, 360.0);
  const star_idx = pymod(Math.trunc(lon / NAKSHATRA_SPAN_DEG), 27);
  return NAKSHATRA_LORDS[star_idx];
}

/**
 * KP four-step significators of the given houses for this chart.
 *
 * A planet signifies a house if it (1) is in the star of an occupant of the house,
 * (2) occupies the house, (3) is in the star of the house owner, or (4) owns the
 * house. Occupancy is by Placidus cusps; ownership is the sign-lord of the cusp.
 * Rahu/Ketu additionally act as agents of their sign dispositor.
 */
export function kp_significators_for_houses(chart: RawChartData, houses: number[]): Set<string> {
  const cusps = chart.placidus_cusps;
  const house_set = new Set(houses.map((h) => Math.trunc(h)));
  if (!house_set.size || !cusps || cusps.length < 12) {
    return new Set();
  }

  // Planet -> Placidus house and star lord. KP uses only the nine grahas; outer
  // planets and Chiron are not significators.
  const house_of: Record<string, number> = {};
  const star_lord_of: Record<string, string> = {};
  for (const name of _GRAHAS) {
    const pos = chart.planets[name];
    if (pos === undefined) {
      continue;
    }
    house_of[name] = placidus_house_of(pos.longitude, cusps);
    star_lord_of[name] = _star_lord_of(pos.longitude);
  }

  const owner_of_house: Record<number, string> = {};
  for (const h of house_set) {
    owner_of_house[h] = SIGN_RULERS[Math.trunc(Math.floor(cusps[h - 1] / 30))];
  }

  const significators = new Set<string>();
  for (const house of house_set) {
    const occupants = Object.entries(house_of)
      .filter(([, occupied]) => occupied === house)
      .map(([planet]) => planet);
    for (const occupant of occupants) {
      significators.add(occupant); // step 2
      for (const [p, sl] of Object.entries(star_lord_of)) {
        if (sl === occupant) significators.add(p); // step 1
      }
    }
    const owner = owner_of_house[house];
    significators.add(owner); // step 4
    for (const [p, sl] of Object.entries(star_lord_of)) {
      if (sl === owner) significators.add(p); // step 3
    }
  }

  // Nodes acting for their dispositor: if Rahu/Ketu's sign-lord signifies, so do they.
  for (const node of ["rahu", "ketu"]) {
    const pos = chart.planets[node];
    if (pos !== undefined && significators.has(SIGN_RULERS[Math.trunc(pos.sign)])) {
      significators.add(node);
    }
  }
  return significators;
}

// KP four-fold significator grades (strongest -> weakest). In Krishnamurti Paddhati a
// planet in the STAR of a house occupant is the strongest significator, then the
// occupant itself, then a planet in the star of the house owner, then the owner.
const _SIG_GRADE_STAR_OF_OCCUPANT = 4.0;
const _SIG_GRADE_OCCUPANT = 3.0;
const _SIG_GRADE_STAR_OF_OWNER = 2.0;
const _SIG_GRADE_OWNER = 1.0;
// Full graha drishti on the bhava (whole-sign/Parashari lens ONLY — KP has no drishti):
// between owner and occupant, an aspecting graha influences the house without sitting in it.
const _SIG_GRADE_ASPECT = 2.0;

/**
 * Each graha's summed KP significator strength for a set of houses.
 *
 * Sums the four-fold grades over every house in the set, so a planet that signifies
 * several of the houses scores higher than one touching a single house. Occupancy is
 * by Placidus cusps; ownership is the sign-lord of the cusp. Nodes inherit the
 * strength of the planet they act for (their sign dispositor), never weaker than it.
 */
export function significator_grades(chart: RawChartData, houses: number[]): Record<string, number> {
  const cusps = chart.placidus_cusps;
  const house_set = new Set(houses.map((h) => Math.trunc(h)));
  const grades: Record<string, number> = Object.fromEntries(_GRAHAS.map((g) => [g, 0.0]));
  if (!house_set.size || !cusps || cusps.length < 12) {
    return grades;
  }

  const house_of: Record<string, number> = {};
  const star_lord_of: Record<string, string> = {};
  for (const name of _GRAHAS) {
    const pos = chart.planets[name];
    if (pos === undefined) {
      continue;
    }
    house_of[name] = placidus_house_of(pos.longitude, cusps);
    star_lord_of[name] = _star_lord_of(pos.longitude);
  }

  for (const house of house_set) {
    const owner = SIGN_RULERS[Math.trunc(Math.floor(cusps[house - 1] / 30))];
    const occupants = Object.entries(house_of)
      .filter(([, h]) => h === house)
      .map(([p]) => p);
    for (const occ of occupants) {
      grades[occ] += _SIG_GRADE_OCCUPANT;
      for (const [p, sl] of Object.entries(star_lord_of)) {
        if (sl === occ) {
          grades[p] += _SIG_GRADE_STAR_OF_OCCUPANT;
        }
      }
    }
    if (owner in grades) {
      grades[owner] += _SIG_GRADE_OWNER;
    }
    for (const [p, sl] of Object.entries(star_lord_of)) {
      if (sl === owner) {
        grades[p] += _SIG_GRADE_STAR_OF_OWNER;
      }
    }
  }

  for (const node of ["rahu", "ketu"]) {
    const pos = chart.planets[node];
    if (pos === undefined) {
      continue;
    }
    const dispositor = SIGN_RULERS[Math.trunc(pos.sign)];
    grades[node] = Math.max(grades[node], grades[dispositor] ?? 0.0);
  }
  return grades;
}

/**
 * Signed KP significator strength per graha: grade for the event's positive
 * houses minus grade for its contra houses.
 *
 * This is the fix for significator saturation. The plain four-fold scheme over a few
 * houses flags 7-9 of the 9 grahas, so every sub-lord/dasha lord is trivially a
 * 'significator' and nothing discriminates birth time. Subtracting the contra houses
 * means a planet that signifies both sides nets ~0 and is correctly treated as
 * non-discriminating; only planets that lean toward the event keep a positive score.
 */
export function net_significator_strength(
  chart: RawChartData,
  positive_houses: number[],
  negative_houses: number[] | null = null
): Record<string, number> {
  const pos = significator_grades(chart, [...positive_houses]);
  let neg: Record<string, number>;
  if (negative_houses && negative_houses.length) {
    neg = significator_grades(chart, [...negative_houses]);
  } else {
    neg = Object.fromEntries(_GRAHAS.map((g) => [g, 0.0]));
  }
  return Object.fromEntries(_GRAHAS.map((g) => [g, (pos[g] ?? 0.0) - (neg[g] ?? 0.0)]));
}

/**
 * WHOLE-SIGN (Rasi/Parashari) significator grades — the same four-fold grading as
 * `significator_grades`, but houses are reckoned by whole signs from the Ascendant sign
 * (house = the Nth sign from the lagna), not by Placidus cusps.
 *
 * Why this exists alongside the Placidus version: KP uses Placidus cusps, but classical
 * Parashari significator/bhava analysis counts houses by whole sign. The two house systems
 * place the same planet in DIFFERENT bhavas (sharply so at high latitudes, where Placidus
 * houses are very unequal), so they are near-independent lenses on the same event. Measured
 * on the AA-rated test set, the whole-sign lens is decorrelated from the Placidus one — where
 * one mis-ranks the true rising sign the other tends to rank it well — so scoring both and
 * combining them discriminates the ascendant far better than either alone.
 */
export function whole_sign_significator_grades(chart: RawChartData, houses: number[]): Record<string, number> {
  const grades: Record<string, number> = Object.fromEntries(_GRAHAS.map((g) => [g, 0.0]));
  const asc = chart.planets["ascendant"];
  const house_set = new Set(houses.map((h) => Math.trunc(h)));
  if (asc === undefined || !house_set.size) {
    return grades;
  }
  const asc_sign = Math.trunc(Number(asc.sign));

  const house_of: Record<string, number> = {};
  const star_lord_of: Record<string, string> = {};
  for (const name of _GRAHAS) {
    const pos = chart.planets[name];
    if (pos === undefined) {
      continue;
    }
    house_of[name] = pymod(Math.trunc(Number(pos.sign)) - asc_sign, 12) + 1;
    star_lord_of[name] = _star_lord_of(pos.longitude);
  }

  for (const house of house_set) {
    const owner = SIGN_RULERS[pymod(asc_sign + house - 1, 12)];
    const occupants = Object.entries(house_of)
      .filter(([, h]) => h === house)
      .map(([p]) => p);
    for (const occ of occupants) {
      grades[occ] += _SIG_GRADE_OCCUPANT;
      for (const [p, sl] of Object.entries(star_lord_of)) {
        if (sl === occ) {
          grades[p] += _SIG_GRADE_STAR_OF_OCCUPANT;
        }
      }
    }
    if (owner in grades) {
      grades[owner] += _SIG_GRADE_OWNER;
    }
    for (const [p, sl] of Object.entries(star_lord_of)) {
      if (sl === owner) {
        grades[p] += _SIG_GRADE_STAR_OF_OWNER;
      }
    }
    // GRAHA DRISHTI on the bhava — Parashari house analysis explicitly reads "aspects received"
    // on the house alongside occupants and the lord (12_synthesis_workflow_principles Phase 5,
    // 05_aspects_drishti). KP deliberately has no drishti, so this tier exists ONLY in the
    // whole-sign (Parashari) lens, never in the Placidus `significator_grades`. FULL aspects
    // only (7th for all; Mars 4/8, Jupiter 5/9, Saturn 3/10 specials): the ¼/½/¾ partials touch
    // nearly every house and would re-saturate the significator set this net model exists to
    // sharpen. Graded between owner (1.0) and occupant (3.0): an aspecting graha influences the
    // bhava but does not sit in it. Added 2026-07-02 (the Rahul case: Jupiter in the lagna
    // aspecting the 7th is a textbook marriage-giver invisible to occupancy/lordship alone).
    for (const [p, h] of Object.entries(house_of)) {
      if (h === house) continue; // occupancy already graded above
      const distance = pymod(house - h, 12) + 1;
      const [, strength] = get_drishti_strength(distance, p);
      if (strength >= 1.0) {
        grades[p] += _SIG_GRADE_ASPECT;
      }
    }
  }

  for (const node of ["rahu", "ketu"]) {
    const pos = chart.planets[node];
    if (pos === undefined) {
      continue;
    }
    const dispositor = SIGN_RULERS[Math.trunc(Number(pos.sign))];
    grades[node] = Math.max(grades[node], grades[dispositor] ?? 0.0);
  }
  return grades;
}

/** Signed whole-sign significator strength: positive-house grades minus contra-house grades. */
export function whole_sign_net_significator_strength(
  chart: RawChartData,
  positive_houses: number[],
  negative_houses: number[] | null = null,
): Record<string, number> {
  const pos = whole_sign_significator_grades(chart, [...positive_houses]);
  let neg: Record<string, number>;
  if (negative_houses && negative_houses.length) {
    neg = whole_sign_significator_grades(chart, [...negative_houses]);
  } else {
    neg = Object.fromEntries(_GRAHAS.map((g) => [g, 0.0]));
  }
  return Object.fromEntries(_GRAHAS.map((g) => [g, (pos[g] ?? 0.0) - (neg[g] ?? 0.0)]));
}

/**
 * JD(UT) of the first sunrise after the given local midnight, or None if the Sun
 * doesn't rise (polar day/night) or the ephemeris call fails. Exported for the
 * sunrise-anchored special points (Vol_01 §3.3 special lagnas, §9.3 Pranapada).
 */
export function sunrise_jd_ut(local_midnight_jd_ut: number, lat: number, lon: number): number | null {
  return _sunrise_jd_ut(local_midnight_jd_ut, lat, lon);
}

// Sunrise depends only on the (date, place) — but `ruling_planets` -> `vaara_lord` runs per
// birth-time CANDIDATE with event-side inputs, so a day-wide scan would repeat the identical
// (expensive) rise_trans search tens of thousands of times. Memoise per (jd, lat, lon), the
// same pattern as special_points._rise_cache (cache stays tiny: one entry per event date/place).
const _sunrise_cache = new Map<string, number | null>();

function _sunrise_jd_ut(local_midnight_jd_ut: number, lat: number, lon: number): number | null {
  try {
    ensure_swiss_ephemeris();
  } catch {
    return null; // ephemeris not initialised yet — do NOT cache this transient failure
  }
  const key = `${local_midnight_jd_ut.toFixed(6)}|${lat}|${lon}`;
  if (_sunrise_cache.has(key)) {
    return _sunrise_cache.get(key)!;
  }
  let value: number | null;
  try {
    const [res, tret] = swe.rise_trans(
      local_midnight_jd_ut,
      swe.SUN,
      swe.CALC_RISE | swe.BIT_DISC_CENTER | swe.BIT_NO_REFRACTION,
      [lon, lat, 0.0]
    );
    value = res !== 0 ? null : tret[0];
  } catch {
    value = null;
  }
  _sunrise_cache.set(key, value);
  return value;
}

/**
 * The weekday (vaara) lord at a moment — with the day boundary at LOCAL SUNRISE,
 * not calendar midnight. The Vedic weekday runs sunrise to sunrise, so a 2 a.m. event
 * belongs to the PREVIOUS day's lord; using the calendar weekday hands every
 * after-midnight event the wrong Ruling Planet. With coordinates the true sunrise is
 * computed via Swiss Ephemeris; without them (or at polar latitudes where the Sun may
 * not rise) a 06:00-local proxy still fixes the common after-midnight case.
 */
export function vaara_lord(
  moment: PlainDateTime,
  lat: number | null = null,
  lon: number | null = null,
  timezone_offset: number | null = null
): string {
  const _proxy = (): string => {
    const effective =
      moment.hour >= 6 ? moment : combine(addDays(dateOnly(moment), -1), timeOnly(moment));
    return _WEEKDAY_LORD[weekday(effective)];
  };

  if (lat === null || lon === null || timezone_offset === null) {
    return _proxy();
  }

  let before_sunrise: boolean;
  try {
    const midnight = combine(dateOnly(moment), { hour: 0, minute: 0, second: 0 });
    const sunrise_jd = _sunrise_jd_ut(local_to_jd(midnight, timezone_offset), lat, lon);
    if (sunrise_jd === null) {
      return _proxy();
    }
    before_sunrise = local_to_jd(moment, timezone_offset) < sunrise_jd;
  } catch {
    return _proxy();
  }
  const effective = before_sunrise
    ? combine(addDays(dateOnly(moment), -1), timeOnly(moment))
    : moment;
  return _WEEKDAY_LORD[weekday(effective)];
}

/**
 * KP Ruling Planets for a moment: day lord (sunrise-based vaara when coordinates
 * are given) plus the sign/star/sub lords of the Ascendant and the Moon. At a genuine
 * event moment these should connect to the natal significators of the activated
 * houses.
 */
export function ruling_planets(
  asc_longitude: number,
  moon_longitude: number,
  moment: PlainDateTime,
  lat: number | null = null,
  lon: number | null = null,
  timezone_offset: number | null = null
): Set<string> {
  const rp = new Set<string>([vaara_lord(moment, lat, lon, timezone_offset)]);
  for (const longitude of [asc_longitude, moon_longitude]) {
    const chain = kp_sublord_chain(longitude);
    rp.add(chain["sign_lord"]);
    rp.add(chain["star_lord"]);
    rp.add(chain["sub_lord"]);
  }
  return rp;
}
